import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    SparklesIcon, UsersIcon, VideoCameraIcon, TicketIcon, 
    ChatBubbleBottomCenterTextIcon, MegaphoneIcon, PaperAirplaneIcon,
    CheckCircleIcon, WifiIcon, CloudArrowUpIcon, TrashIcon
} from '@heroicons/react/24/outline';
import IosCard from '../../components/iOSCard'; 
import { db } from '../../firebase'; 
import { 
    collection, getDocs, addDoc, serverTimestamp, query, 
    orderBy, limit, deleteDoc, doc, onSnapshot 
} from 'firebase/firestore';
import * as XLSX from 'xlsx';
import StudentActivityMonitor from './StudentActivityMonitor';

const Dashboard = () => {
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [showPublishSuccess, setShowPublishSuccess] = useState(false); 
    const [newAnnouncement, setNewAnnouncement] = useState(""); 
    const [latestStudents, setLatestStudents] = useState([]);
    const [announcements, setAnnouncements] = useState([]); 
    const [systemStatus, setSystemStatus] = useState({ online: true, lastSync: "جاري الفحص..." });
    const [stats, setStats] = useState({ totalStudents: 0, totalLessons: 0, activeTokens: 0, totalComments: 0 });

    useEffect(() => {
        const handleOnline = () => setSystemStatus(prev => ({ ...prev, online: true }));
        const handleOffline = () => setSystemStatus(prev => ({ ...prev, online: false }));
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        const unsubAnnounce = onSnapshot(query(collection(db, "notifications"), orderBy("createdAt", "desc")), (snapshot) => {
            setAnnouncements(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const fetchAllData = async () => {
            try {
                const usersSnapshot = await getDocs(collection(db, "users"));
                const lessonsSnapshot = await getDocs(collection(db, "lessons"));
                const tokensSnapshot = await getDocs(collection(db, "tokens"));
                const chatsSnapshot = await getDocs(collection(db, "chats"));

                setStats({
                    totalStudents: usersSnapshot.size,
                    totalLessons: lessonsSnapshot.size,
                    activeTokens: tokensSnapshot.size,
                    totalComments: chatsSnapshot.size, 
                });

                const studentsQuery = query(collection(db, "users"), orderBy("createdAt", "desc"), limit(5));
                const studentsSnap = await getDocs(studentsQuery);
                setLatestStudents(studentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                
                setSystemStatus(prev => ({ ...prev, lastSync: new Date().toLocaleTimeString('ar-EG') }));
            } catch (error) {
                console.error("Fetch fail:", error);
            }
        };

        fetchAllData();
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            unsubAnnounce(); 
        };
    }, []);

    const deleteAnnouncement = async (id) => {
        if (window.confirm("هل أنت متأكد من حذف هذا الخبر؟ سيختفي من عند جميع الطلاب فوراً.")) {
            try {
                await deleteDoc(doc(db, "notifications", id));
            } catch (e) {
                console.error("Error deleting:", e);
            }
        }
    };

    const downloadBackup = async () => {
        try {
            const tokenPriceIQD = 25000; 
            const tokensSnap = await getDocs(collection(db, "tokens"));
            const allTokens = tokensSnap.docs.map(doc => doc.data());
            
            const usedTokensCount = allTokens.filter(t => t.isUsed === true || t.status === 'used').length;
            const remainingTokensCount = allTokens.length - usedTokensCount;
            const totalRevenue = usedTokensCount * tokenPriceIQD;

            const financialStats = [
                { "الوصف": "تاريخ استخراج التقرير", "القيمة": new Date().toLocaleString('ar-IQ') },
                { "الوصف": "إجمالي الطلاب المسجلين", "القيمة": stats.totalStudents },
                { "الوصف": "إجمالي الأكواد المولدة بالنظام", "القيمة": allTokens.length },
                { "الوصف": "عدد الأكواد المباعة (المستخدمة)", "القيمة": usedTokensCount },
                { "الوصف": "عدد الأكواد المتبقية (غير المستخدمة)", "القيمة": remainingTokensCount },
                { "الوصف": "سعر الكود الواحد المعتمد", "القيمة": `${tokenPriceIQD.toLocaleString()} د.ع` },
                { "الوصف": "إجمالي المبالغ الواردة (الأرباح)", "القيمة": `${totalRevenue.toLocaleString()} دينار عراقي` }
            ];

            const studentsSnap = await getDocs(collection(db, "users"));
            const studentsData = studentsSnap.docs.map(doc => ({
                "الاسم الكامل": doc.data().name || "طالب جديد",
                "البريد الإلكتروني": doc.data().email || "غير متوفر",
                "حالة الاشتراك": (doc.data().isSubscribed || doc.data().active) ? 'مشترك' : 'غير مشترك',
                "تاريخ الانضمام": doc.data().createdAt?.toDate ? doc.data().createdAt.toDate().toLocaleDateString('ar-IQ') : "تاريخ قديم"
            }));

            const wb = XLSX.utils.book_new();
            const wsFinancial = XLSX.utils.json_to_sheet(financialStats);
            const wsStudents = XLSX.utils.json_to_sheet(studentsData);
            XLSX.utils.book_append_sheet(wb, wsFinancial, "الجرد المالي");
            XLSX.utils.book_append_sheet(wb, wsStudents, "سجل الطلاب");
            XLSX.writeFile(wb, `تقرير_نور_أكاديمي_${new Date().toLocaleDateString('ar-IQ')}.xlsx`);
        } catch (error) {
            console.error("Financial Backup Error:", error);
        }
    };

    const postGlobalUpdate = async () => {
    if (!newAnnouncement.trim()) return;
    try {
        // نرسل الإشعار بجسم كامل يخدم الغرضين (الجرس + شريط الأخبار)
        await addDoc(collection(db, "notifications"), {
            message: newAnnouncement,       // هذا يظهر في شريط الأخبار
            text: "تحديث جديد: " + newAnnouncement, // هذا يظهر في الجرس (Navbar)
            createdAt: serverTimestamp(),
            type: 'all',                  // يظهر للكل
            role: 'student',              // يحدد فئة المستهدف
            targetId: 'all',              // يخلي الجرس يدق عند الكل
            isRead: false                 // ضروري جداً عشان الجرس يتفعل
        });

        setNewAnnouncement(""); 
        setShowPublishSuccess(true); 
        setSystemStatus(prev => ({ ...prev, lastSync: new Date().toLocaleTimeString('ar-EG') }));
    } catch (e) { 
        console.error("خطأ في النشر:", e); 
    }
};

    return (
        <div style={{ fontFamily: "'Cairo', sans-serif" }} className="relative space-y-8 animate-in fade-in duration-700 text-right font-cairo" dir="rtl">
            
            <AnimatePresence>
                {showPublishSuccess && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[10000] flex items-center justify-center p-4 backdrop-blur-xl bg-black/20">
                        <motion.div initial={{ scale: 0.8, y: 50 }} animate={{ scale: 1, y: 0 }} className="ios-glass max-w-sm w-full p-10 rounded-[50px] border border-white/40 shadow-2xl text-center space-y-6 bg-white/95 dark:bg-[#1C1C1E]/95">
                            <div className="w-20 h-20 bg-emerald-500/10 rounded-[28px] flex items-center justify-center text-emerald-500 mx-auto">
                                <CheckCircleIcon className="w-12 h-12" />
                            </div>
                            <h3 className="text-2xl font-black text-gray-900 dark:text-white leading-none">تم النشر بنجاح!</h3>
                            <button onClick={() => setShowPublishSuccess(false)} className="w-full py-4 bg-[#007AFF] text-white rounded-3xl font-black shadow-lg active:scale-95 transition-all">ممتاز</button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h2 className="text-3xl font-black text-gray-800 dark:text-white tracking-tight">نظرة عامة 📊</h2>
                <button onClick={downloadBackup} className="flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-[#007AFF] hover:text-white text-[#007AFF] border border-[#007AFF]/20 rounded-2xl transition-all font-black text-xs sm:text-sm active:scale-95 shadow-sm">
                    <CloudArrowUpIcon className="w-5 h-5 rotate-180" />
                    <span>سحب التقرير المالي (Excel)</span>
                </button>
            </div>

            <div className="ios-glass p-8 rounded-[40px] border border-white/40 shadow-xl bg-gradient-to-br from-blue-500/5 to-purple-500/5 backdrop-blur-xl">
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-blue-500 rounded-2xl text-white shadow-lg shadow-blue-200"><MegaphoneIcon className="w-6 h-6" /></div>
                    <h3 className="text-xl font-black text-gray-800 dark:text-white leading-none">مركز الإعلانات العام 📣</h3>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                    <input type="text" value={newAnnouncement} onChange={(e) => setNewAnnouncement(e.target.value)} placeholder="ما هو الخبر الجديد اليوم؟" className="flex-1 p-5 rounded-3xl bg-white/50 dark:bg-black/20 border-2 border-transparent focus:border-blue-500 outline-none font-bold text-sm text-right dark:text-white" />
                    <button onClick={postGlobalUpdate} className="bg-blue-600 text-white px-8 py-5 rounded-3xl font-black text-sm shadow-xl active:scale-95 transition-all">نشر الآن</button>
                </div>

                <div className="space-y-4 border-t border-gray-100 dark:border-white/5 pt-6">
                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-2">الأخبار المنشورة حالياً (للحذف)</h4>
                    <div className="grid grid-cols-1 gap-3">
                        {announcements.filter(a => a.type === 'all').slice(0, 5).map((news) => (
                            <div key={news.id} className="flex justify-between items-center p-4 bg-white/40 dark:bg-white/5 rounded-3xl border border-white/20 group transition-all hover:bg-white/60">
                                <p className="text-sm font-bold text-gray-700 dark:text-gray-200 truncate ml-4">{news.message}</p>
                                <button 
                                    onClick={() => deleteAnnouncement(news.id)}
                                    className="p-2 text-red-500 hover:bg-red-500/10 rounded-xl transition-all active:scale-90"
                                >
                                    <TrashIcon className="w-5 h-5" />
                                </button>
                            </div>
                        ))}
                        {announcements.filter(a => a.type === 'all').length === 0 && (
                            <p className="text-center text-xs text-gray-400 py-2 italic">لا توجد أخبار منشورة حالياً</p>
                        )}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <IosCard title="إجمالي الطلاب" color="blue" icon={<UsersIcon className="w-6 h-6"/>}><p className="text-3xl font-black text-gray-800 dark:text-white">{stats.totalStudents}</p></IosCard>
                <IosCard title="المحاضرات" color="emerald" icon={<VideoCameraIcon className="w-6 h-6"/>}><p className="text-3xl font-black text-gray-800 dark:text-white">{stats.totalLessons}</p></IosCard>
                <IosCard title="الأكواد المفعلة" color="purple" icon={<TicketIcon className="w-6 h-6"/>}><p className="text-3xl font-black text-gray-800 dark:text-white">{stats.activeTokens}</p></IosCard>
                <IosCard title="المناقشات" color="orange" icon={<ChatBubbleBottomCenterTextIcon className="w-6 h-6"/>}><p className="text-3xl font-black text-gray-800 dark:text-white">{stats.totalComments}</p></IosCard>
            </div>

            {/* البطاقات السفلية (المنضمين + مراقبة النشاط + حالة النظام) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 text-right">
                
                {/* بطاقة آخر المنضمين */}
                <IosCard title="آخر المنضمين" color="cyan" icon={<UsersIcon className="w-6 h-6"/>}>
                    <div className="divide-y divide-gray-100 dark:divide-white/5">
                        {latestStudents.map((student) => (
                            <div key={student.id} className="flex justify-between items-center py-4">
                                <span className="font-bold text-gray-700 dark:text-gray-300 text-sm">{student.name || "طالب جديد"}</span>
                                <span className="text-[10px] text-gray-400 font-bold uppercase">{student.createdAt?.toDate ? new Date(student.createdAt.toDate()).toLocaleDateString('ar-EG') : 'انضم حديثاً'}</span>
                            </div>
                        ))}
                    </div>
                </IosCard>

                {/* بطاقة مراقبة النشاط */}
                <StudentActivityMonitor />

                {/* بطاقة حالة النظام */}
                <div className="lg:col-span-2">
                    <IosCard title="حالة استقرار النظام" color={systemStatus.online ? "emerald" : "red"} icon={<WifiIcon className="w-6 h-6"/>}>
                        <div className="py-6 space-y-6 text-center">
                            <div className="flex justify-center items-center gap-4">
                                <div className={`w-3 h-3 rounded-full ${systemStatus.online ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`}></div>
                                <span className="font-black text-gray-800 dark:text-white text-lg">
                                    {systemStatus.online ? "خادم Firebase متصل" : "عذراً، لا يوجد اتصال!"}
                                </span>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-4 bg-gray-50 dark:bg-white/5 rounded-3xl border border-gray-100 dark:border-white/5 shadow-inner">
                                    <CloudArrowUpIcon className="w-5 h-5 mx-auto mb-2 text-blue-500" />
                                    <p className="text-[10px] font-black text-gray-400 uppercase">آخر مزامنة</p>
                                    <p className="text-sm font-bold dark:text-white">{systemStatus.lastSync}</p>
                                </div>
                                <div className="p-4 bg-gray-50 dark:bg-white/5 rounded-3xl border border-gray-100 dark:border-white/5 shadow-inner">
                                    <CheckCircleIcon className="w-5 h-5 mx-auto mb-2 text-emerald-500" />
                                    <p className="text-[10px] font-black text-gray-400 uppercase">قاعدة البيانات</p>
                                    <p className="text-sm font-bold dark:text-white">مؤمنة ✓</p>
                                </div>
                            </div>
                        </div>
                    </IosCard>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;